/**
 * Month calendar — the availability-at-a-glance view.
 *
 * Panel Quote's calendar counted vehicles per day; this keeps that but adds
 * what a front desk actually asks each morning: who is collecting today and
 * who is due back.
 */

import React, { useMemo } from 'react';
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWeekend,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';

import { bookingInterval, bookingVehicleId, dailyAvailability } from '../../lib/fleet';
import { cn } from '../../lib/utils';

const WEEK_STARTS_ON = 1; // Monday, as in the Panel Quote calendar

/**
 * How tall a day is. As close to square as the screen allows: never taller than
 * a day is wide, and never so tall that the month runs off a desktop screen.
 *
 * - 18rem is everything above and below the grid on the Fleet page: its
 *   padding, title, tabs, filter card and the weekday row.
 * - 19rem is the sidebar and page padding, so (100vw - 19rem) / 7 is about the
 *   width of a day.
 * - 6rem is the floor, what a day's contents need. Below it (a short laptop
 *   screen) the month scrolls rather than squashing the days.
 */
const dayHeight = (weeks) =>
  `max(6rem, min(calc((100vh - 18rem) / ${weeks}), calc((100vw - 19rem) / 7)))`;

const FleetCalendar = ({ vehicles, bookings, month, now = new Date(), onSelectDay }) => {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: WEEK_STARTS_ON });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: WEEK_STARTS_ON });
    return eachDayOfInterval({ start, end });
  }, [month]);

  const availability = useMemo(
    () => dailyAvailability(vehicles, bookings, days, now),
    [vehicles, bookings, days, now]
  );

  /** Collections and returns keyed by day, so each cell can show its movements. */
  const movements = useMemo(() => {
    const map = new Map();
    const bump = (day, key) => {
      const id = format(day, 'yyyy-MM-dd');
      const entry = map.get(id) || { pickups: 0, returns: 0 };
      entry[key] += 1;
      map.set(id, entry);
    };

    bookings.forEach((b) => {
      if (b.type === 'maintenance' || b.status === 'cancelled') return;
      if (!bookingVehicleId(b)) return;
      const { start, end, openEnded } = bookingInterval(b, now);
      if (start) bump(start, 'pickups');
      if (end && !openEnded) bump(end, 'returns');
    });

    return map;
  }, [bookings, now]);

  const weekdayLabels = days.slice(0, 7).map((d) => format(d, 'EEE'));
  const minHeight = dayHeight(Math.ceil(days.length / 7));

  return (
    <div>
      <div className="grid grid-cols-7 border-b">
        {weekdayLabels.map((label) => (
          <div key={label} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {availability.map(({ day, total, available }) => {
          const inMonth = isSameMonth(day, month);
          const isToday = isSameDay(day, now);
          const move = movements.get(format(day, 'yyyy-MM-dd'));
          const fullyBooked = total > 0 && available === 0;

          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelectDay?.(day)}
              style={{ minHeight }}
              className={cn(
                'relative flex flex-col gap-1 border-b border-r p-2 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                inMonth ? 'hover:bg-accent/50' : 'bg-muted/30 text-muted-foreground hover:bg-muted/50',
                isWeekend(day) && inMonth && 'bg-muted/20'
              )}
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-sm tabular-nums',
                    isToday && 'bg-primary font-bold text-primary-foreground',
                    !isToday && inMonth && 'font-medium'
                  )}
                >
                  {format(day, 'd')}
                </span>
              </div>

              {inMonth && total > 0 && (
                <>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      fullyBooked ? 'text-amber-700 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'
                    )}
                  >
                    {fullyBooked ? 'All booked' : `${available} of ${total} free`}
                  </span>

                  {/* Utilisation bar — filled portion is what is out that day. */}
                  <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn('h-full rounded-full', fullyBooked ? 'bg-amber-500' : 'bg-emerald-500')}
                      style={{ width: `${total ? ((total - available) / total) * 100 : 0}%` }}
                    />
                  </div>

                  {move && (
                    <div className="mt-auto flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                      {move.pickups > 0 && (
                        <span className="inline-flex items-center gap-0.5" title={`${move.pickups} collecting`}>
                          <ArrowUpRight className="h-3 w-3 text-sky-500" />
                          {move.pickups}
                        </span>
                      )}
                      {move.returns > 0 && (
                        <span className="inline-flex items-center gap-0.5" title={`${move.returns} due back`}>
                          <ArrowDownLeft className="h-3 w-3 text-emerald-500" />
                          {move.returns}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FleetCalendar;
